import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Receipt, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Smartphone, 
  Signal, 
  CreditCard,
  ArrowLeft,
  RefreshCcw,
  Calendar,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Shield,
  AlertTriangle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SubscriptionRequest from '../requests/vendor/SubscriptionRequest';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { usePermissionCheck } from '../hooks/permissionHooks/usePermissionCheck';
import { PERMISSIONS } from '../config/permissions';

const PaymentHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedNetwork, setSelectedNetwork] = useState('all');
  const itemsPerPage = 5;
  const navigate = useNavigate();

  // Permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  // Check specific permissions
  const canViewSubscriptionHistory = canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY) || isMainVendor;

  // Debug logging for permission checks
  console.log('PaymentHistory Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canViewSubscriptionHistory
  });

  useEffect(() => {
    if (canViewSubscriptionHistory) {
      fetchTransactionHistory();
    }
  }, [canViewSubscriptionHistory]);

  const fetchTransactionHistory = async () => {
    if (!canViewSubscriptionHistory) return; // Don't fetch if no permission
    
    try {
      setIsLoading(true);
      const response = await SubscriptionRequest.fetchUserSubscriptionHistory();
      if (response?.data?.status_code === "000" && !response?.data?.in_error) {
        setTransactions(response.data.data);
      } else {
        toast.error("Failed to fetch payment history");
      }
    } catch (error) {
      toast.error("An error occurred while fetching payment history");
    } finally {
      setIsLoading(false);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.4 }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-100';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.msisdn.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.transaction_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.network.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply network filter
    if (selectedNetwork !== 'all') {
      filtered = filtered.filter(t => 
        t.network.toLowerCase() === selectedNetwork.toLowerCase()
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const filteredTransactions = filterTransactions();
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const networkOptions = [
    { value: 'all', label: 'All Networks' },
    { value: 'MTN', label: 'MTN' },
    { value: 'TELECEL', label: 'Telecel' },
    { value: 'AIRTELTIGO', label: 'AirtelTigo' }
  ];

  // Access denied screen for users without history permissions
  if (!isAuthenticated || !canViewSubscriptionHistory) {
    return (
      <div className="bg-white">
        <Navbar />
        <div className="flex justify-center items-center min-h-[80vh] bg-gray-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to view subscription history. Please contact your administrator to request access to the Subscription History feature.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">View Subscription History in Subscription Management</p>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/subscription')}
                className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg transition-colors hover:bg-gray-600"
              >
                View Subscription
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF6C6C]/90 transition-colors font-medium"
              >
                Return to Dashboard
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F5] via-white to-[#FFF0F0]">
      <Navbar />
      
      <motion.div 
        className="px-4 py-8 mx-auto max-w-7xl sm:py-16 sm:px-6 lg:px-8"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
            <div className="flex items-center space-x-4">
              <motion.button 
                onClick={() => navigate(-1)}
                className="p-2 text-gray-600 bg-white rounded-lg shadow-sm hover:bg-[#FFF0F0] hover:text-[#FF6C6C] transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>
              <h1 className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-[#FF6C6C]">
                Payment History
              </h1>
            </div>
            <motion.button
              onClick={fetchTransactionHistory}
              className="flex items-center justify-center px-4 py-2 space-x-2 text-[#FF6C6C] bg-white rounded-lg shadow-sm hover:bg-[#FFF0F0] w-full sm:w-auto group transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCcw className="w-4 h-4 transition-transform duration-500 group-hover:rotate-180" />
              <span>Refresh</span>
            </motion.button>
          </div>
        </motion.div>

        {/* New Filter Section */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm"
        >
          <div className="flex flex-col gap-4 sm:flex-row">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
              />
            </div>

            {/* Network Filter */}
            <div className="relative min-w-[160px]">
              <select
                value={selectedNetwork}
                onChange={(e) => setSelectedNetwork(e.target.value)}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
              >
                {networkOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Filter className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Sort Toggle */}
            <motion.button
              onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
              className="flex items-center justify-center px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-[#FFF0F0] hover:text-[#FF6C6C] transition-colors duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {sortOrder === 'newest' ? (
                <SortDesc className="w-4 h-4" />
              ) : (
                <SortAsc className="w-4 h-4" />
              )}
              <span className="ml-2">{sortOrder === 'newest' ? 'Newest' : 'Oldest'}</span>
            </motion.button>
          </div>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="relative w-12 h-12 sm:w-16 sm:h-16">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-[#FFE5E5] rounded-full animate-ping opacity-75"></div>
              <div className="absolute top-0 left-0 w-full h-full border-4 border-[#FFE5E5] border-t-[#FF6C6C] rounded-full animate-spin"></div>
            </div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            {currentTransactions.map((transaction, index) => (
              <motion.div
                key={transaction.transaction_id}
                variants={itemVariants}
                className="p-4 sm:p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-[#FF6C6C]/20"
                whileHover={{ y: -2 }}
              >
                <div className="flex flex-col space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-4">
                      <div className="p-2 sm:p-3 bg-gradient-to-br from-[#FFF5F5] to-[#FFF0F0] rounded-lg shrink-0 shadow-sm">
                        <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6C6C]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-500 sm:text-sm">Transaction ID</p>
                        <p className="text-sm sm:text-lg font-semibold text-gray-900 truncate group-hover:text-[#FF6C6C] transition-colors duration-300">
                          {transaction.transaction_id}
                        </p>
                      </div>
                    </div>
                    <motion.div 
                      className="hidden items-center space-x-1 text-xs text-gray-500 sm:flex"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(transaction.created_at || new Date())}</span>
                    </motion.div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center sm:gap-6">
                    <div className="flex items-center px-3 py-1.5 space-x-2 bg-gray-50 rounded-lg">
                      <Smartphone className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-600 truncate sm:text-sm">
                        {transaction.msisdn}
                      </span>
                    </div>
                    <div className="flex items-center px-3 py-1.5 space-x-2 bg-gray-50 rounded-lg">
                      <Signal className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-600 sm:text-sm">
                        {transaction.network}
                      </span>
                    </div>
                    <div className="flex items-center px-3 py-1.5 space-x-2 bg-emerald-50 rounded-lg">
                      <CreditCard className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm font-bold text-gray-900 sm:text-lg">
                        ₵{transaction.amount}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1.5 space-x-2 text-xs sm:text-sm font-medium border rounded-lg transition-colors duration-300 ${getStatusStyle(transaction.status)}`}>
                      {getStatusIcon(transaction.status)}
                      <span>{transaction.status}</span>
                      <ArrowUpRight className="w-3 h-3 opacity-50" />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}

            {currentTransactions.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center py-12 sm:py-16"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full opacity-50 blur-xl"></div>
                  <Receipt className="relative w-12 h-12 text-indigo-400 sm:w-16 sm:h-16" />
                </div>
                <h3 className="mt-4 text-base font-medium text-gray-900 sm:text-lg">
                  No transactions yet
                </h3>
                <p className="px-4 mt-1 max-w-sm text-xs text-center text-gray-500 sm:text-sm">
                  Your payment history will appear here once you make your first transaction
                </p>
              </motion.div>
            )}

            {/* New Pagination Section */}
            {filteredTransactions.length > 0 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col gap-4 justify-between items-center px-4 py-3 mt-6 bg-white rounded-xl border border-gray-100 shadow-sm sm:flex-row"
              >
                <p className="text-sm text-gray-600">
                  Showing {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} transactions
                </p>
                
                <div className="flex gap-2 items-center">
                  <motion.button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FFF0F0] hover:text-[#FF6C6C] hover:border-[#FF6C6C] transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </motion.button>
                  
                  <div className="flex gap-1 items-center">
                    {[...Array(totalPages)].map((_, idx) => (
                      <motion.button
                        key={idx + 1}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors duration-300
                          ${currentPage === idx + 1 
                            ? 'bg-[#FF6C6C] text-white' 
                            : 'bg-white text-gray-600 hover:bg-[#FFF0F0] hover:text-[#FF6C6C]'
                          }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {idx + 1}
                      </motion.button>
                    ))}
                  </div>
                  
                  <motion.button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#FFF0F0] hover:text-[#FF6C6C] hover:border-[#FF6C6C] transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </motion.button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentHistory; 